/**
 * useReaderLinkOrigin.ts
 *
 * When the user opens a Book-CAIR reader link from the chat — an inline citation
 * pill (the `.ns-cite` anchor in the AI message HTML, resolved server-side to a
 * bare reader URL) or a "Sources" card — we want the reader to (a) know where the
 * user came from and (b) be logged in.
 *
 * This document-level click listener intercepts clicks on internal reader links
 * and opens them in a new tab with:
 *   - the origin params (cair_origin / cair_host via aiChatOriginQuery) so the
 *     reader's "Go to AI Chat" link returns the user to where they came from; and
 *   - when the chat is embedded cross-site (where the reader's first-party tab
 *     can't see the iframe's session), the chat's bearer token, which the reader
 *     exchanges for a first-party session (ReaderTokenGate -> loginWithJwt) and
 *     then strips from its URL.
 *
 * External citation links (a source's own publisher site) aren't reader URLs, so
 * they're left untouched.
 */

import { useEffect } from 'react'
import { aiChatOriginQuery } from '@/utilities/aiChatOrigin'
import { readAuthToken } from '@/utilities/authToken'
import { isThirdPartyContext } from '@/utilities/browserDetection'

// Matches our internal Book-CAIR reader URLs by path, e.g.
// /live-book-cair-desktop/<id> or /live-book-cair-mobile/<id>. We match on the
// path (not the host) because the backend resolves pill links to its own origin,
// which differs from the page host in local dev (chat on localhost, reader on
// staging). External citation links (a source's own publisher site) don't have
// this path, so they're naturally left alone.
const isReaderUrl = (href: string): boolean => {
    try {
        const u = new URL(href, window.location.href)
        return /\/live-book-cair-/.test(u.pathname)
    } catch {
        return false
    }
}

export const useReaderLinkOrigin = (circleId?: string): void => {
    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            // Let modified / non-primary clicks use the browser default.
            if (
                e.defaultPrevented ||
                e.button !== 0 ||
                e.metaKey ||
                e.ctrlKey ||
                e.shiftKey ||
                e.altKey
            ) {
                return
            }

            const target = e.target as HTMLElement | null
            const anchor = target && target.closest('a')
            if (!anchor) return
            const href = (anchor as HTMLAnchorElement).href
            if (!href || !isReaderUrl(href)) return

            e.preventDefault()
            const u = new URL(href)
            // Ensure the origin params are present (pills lack them; Sources cards
            // already have them — set is idempotent).
            const origin = new URLSearchParams(aiChatOriginQuery(circleId).replace(/^\?/, ''))
            origin.forEach((value, key) => u.searchParams.set(key, value))
            // Embedded (cross-site) only: the first-party reader tab can't see the
            // iframe's session, so hand it the chat's bearer token to log itself in.
            // Standalone/first-party keeps its own cookie, so no token is needed.
            if (isThirdPartyContext()) {
                const token = readAuthToken()
                if (token) u.searchParams.set('token', token)
            }
            window.open(u.toString(), '_blank')
        }

        document.addEventListener('click', onClick)
        return () => document.removeEventListener('click', onClick)
    }, [circleId])
}
